import { makeAutoObservable } from 'mobx';

export type DownloadStatus =
  | 'pending'
  | 'downloading'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface DownloadItem {
  id: string;
  label: string;
  filename: string;
  status: DownloadStatus;
  receivedBytes: number;
  totalBytes: number | null;
  error?: string;
  abort: () => void;
}

let counter = 0;
const nextId = (): string => {
  counter += 1;
  return `dl-${Date.now()}-${counter}`;
};

class DownloadStore {
  public items: DownloadItem[] = [];
  public panelOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  public get activeCount(): number {
    return this.items.filter(
      i => i.status === 'pending' || i.status === 'downloading'
    ).length;
  }

  public setPanelOpen = (open: boolean): void => {
    this.panelOpen = open;
  };

  public createItem = (
    label: string,
    filename: string,
    abort: () => void
  ): DownloadItem => {
    const item: DownloadItem = {
      id: nextId(),
      label,
      filename,
      status: 'pending',
      receivedBytes: 0,
      totalBytes: null,
      abort,
    };
    this.items = [...this.items, item];
    return item;
  };

  public updateItem = (id: string, patch: Partial<DownloadItem>): void => {
    this.items = this.items.map(i => (i.id === id ? { ...i, ...patch } : i));
  };

  public removeItem = (id: string): void => {
    this.items = this.items.filter(i => i.id !== id);
  };

  public clearFinished = (): void => {
    this.items = this.items.filter(
      i => i.status === 'pending' || i.status === 'downloading'
    );
  };

  public cancel = (id: string): void => {
    const item = this.items.find(i => i.id === id);
    if (!item) return;
    if (item.status === 'pending' || item.status === 'downloading') {
      item.abort();
    }
  };
}

export const downloadStore = new DownloadStore();
